import { createContext, useContext, useState, useMemo, useRef, useEffect } from 'react';
import * as React from 'react';
import { SaveClientFlags, ClientFlags } from 'sling-sdk/src/authentication/types';

type SettingsClientsFlagsContextType = {
  clientFlags: ClientFlags;
  initialClientFlags: ClientFlags;
  onClientFlagsChange: (clientflags: Partial<ClientFlags>) => void;
  isClientFlagsChanged: boolean;
  setIsClientFlagsChanged: (value: boolean) => void;
  revertClientFlagsChanges: () => void;
  saveClientFlags: SaveClientFlags;
  saveAllClientFlags: () => void;
};

const SettingsClientsFlagsContext = createContext<SettingsClientsFlagsContextType>({
  clientFlags: null,
  onClientFlagsChange: null,
  isClientFlagsChanged: null,
  setIsClientFlagsChanged: null,
  revertClientFlagsChanges: null,
  initialClientFlags: null,
  saveClientFlags: null,
  saveAllClientFlags: null,
});

type Props = {
  clientFlags: ClientFlags;
  saveClientFlags: SaveClientFlags;
};

const SettingsClientsFlagsProvider: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  clientFlags: originalClientFlags,
  saveClientFlags,
}) => {
  const initialClientFlags = useRef(originalClientFlags);
  const [clientFlags, setClientFlags] = useState(originalClientFlags);
  const [isClientFlagsChanged, setIsClientFlagsChanged] = useState(false);

  useEffect(() => {
    setClientFlags(originalClientFlags);
    initialClientFlags.current = originalClientFlags;
  }, [originalClientFlags]);

  useEffect(() => {
    setIsClientFlagsChanged(
      JSON.stringify(initialClientFlags.current) !== JSON.stringify(clientFlags)
    );
  }, [clientFlags]);

  const value = useMemo(() => {
    const onClientFlagsChange: (clientflags: Partial<ClientFlags>) => void = (
      updatedClientFlagsProperties
    ): void => {
      const updatedUserOrgSettings = {
        ...clientFlags,
        ...updatedClientFlagsProperties,
      };

      setClientFlags(updatedUserOrgSettings);
    };

    const revertClientFlagsChanges = (): void => setClientFlags(initialClientFlags.current);
    const saveAllClientFlags = () => saveClientFlags({ flags: clientFlags });

    return {
      clientFlags,
      onClientFlagsChange,
      isClientFlagsChanged,
      setIsClientFlagsChanged,
      revertClientFlagsChanges,
      initialClientFlags: initialClientFlags.current,
      saveClientFlags,
      saveAllClientFlags,
    };
  }, [clientFlags, isClientFlagsChanged, saveClientFlags]);

  return (
    <SettingsClientsFlagsContext.Provider value={value}>
      {children}
    </SettingsClientsFlagsContext.Provider>
  );
};

function useSettingsClientFlags(): SettingsClientsFlagsContextType {
  const context = useContext(SettingsClientsFlagsContext);
  if (!context) {
    throw new Error('SettingsClientsFlagsContext must be used within a ClientsFlagsProvider');
  }

  return context;
}

export { SettingsClientsFlagsProvider, useSettingsClientFlags };
